import { useMemo, useEffect } from 'react';
import {
  TextField,
  TableRow,
  TableCell,
  IconButton,
  Button,
  Select,
  MenuItem,
  Box,
  Stack,
} from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import { useInvoice } from '../common/InvoiceProvider';
import ClearIcon from '@mui/icons-material/Clear';
import { AppliesTo } from 'common/constants';

const NewInvoiceItemTableRow = (props) => {
  const { taxesValues, input, setInput, errors, setErrors } = useInvoice();

  // Filter out empty/placeholder tax entries (id === '' or 0) before rendering
  const invoiceItemTaxes = useMemo(() => {
    return (props?.invoiceItemTaxes ?? []).filter((tax) => tax.id !== '' && tax.id !== 0 && tax.id !== null);
  }, [props?.invoiceItemTaxes]);

  const taxesList = useMemo(() => {
    return invoiceItemTaxes;
  }, [invoiceItemTaxes]);

  useEffect(() => {
    let taxSum = 0;
    invoiceItemTaxes.forEach((tax) => {
      if (tax.rate > 0) {
        taxSum += (Number(tax.rate) * Number(props?.amount)) / 100;
      } else {
        taxSum += Number(tax.amount);
      }
    });
    setInput({
      ...input,
      invoiceItems: input?.invoiceItems?.map((item) =>
        item.id === props.id ? { ...item, taxesSum: taxSum } : item
      ),
    });
  }, [props?.invoiceItemTaxes, props?.amount]);

  // Mark an item field as reviewed (clears confidence flags) without changing its value
  const handleItemFieldReview = (fieldKey: string) => {
    setErrors((prevErrors) => {
      if (prevErrors?.[fieldKey] === 'missing confidence' || prevErrors?.[fieldKey] === 'low confidence') {
        return { ...prevErrors, [fieldKey]: '' };
      }
      return prevErrors;
    });
  };

  const handleTaxChange = function (e, itemId, taxIndex) {
    const updatedTax = {
      id: e.target.value,
      rate: itemTaxes?.find((tax) => tax.id === e.target.value)?.rate,
      amount: itemTaxes?.find((tax) => tax.id === e.target.value)?.amount,
      name: itemTaxes?.find((tax) => tax.id === e.target.value)?.name,
    };
    if (taxesList.some((tax) => tax.id === e.target.value)) {
      return;
    }
    setInput({
      ...input,
      invoiceItems: input?.invoiceItems?.map((item) =>
        item.id === itemId
          ? {
              ...item,
              invoiceItemTaxes: item?.invoiceItemTaxes?.map((tax, index) =>
                index === taxIndex
                  ? {
                      ...tax,
                      ...updatedTax,
                    }
                  : tax
              ),
            }
          : item
      ),
    });
    setErrors({
      ...errors,
      [`invoiceItems[${itemId}].invoiceItemTaxes[${taxIndex + 1}].name`]: '',
    });
  };

  const handleDeleteTax = function (itemIndex) {
    setInput({
      ...input,
      invoiceItems: input?.invoiceItems?.map((item) =>
        item.id === props.id
          ? {
              ...item,
              invoiceItemTaxes: item?.invoiceItemTaxes?.filter((_, index) => index !== itemIndex),
            }
          : item
      ),
    });
    setErrors({
      ...errors,
      [`invoiceItems[${props.id}].invoiceItemTaxes[${itemIndex + 1}].name`]: '',
    });
  };

  const itemTaxes = taxesValues?.filter(
    (tax) => tax.appliesTo === AppliesTo.Item || tax.appliesTo === AppliesTo.All
  );

  const handleNewTax = function (itemId) {
    if (taxesList?.length >= itemTaxes?.length) {
      return;
    }
    const mappedTaxIds = taxesList?.map((tax) => tax.id);
    const defaultTax = itemTaxes?.find((tax) => !mappedTaxIds?.includes(tax['id']));
    const newItem = {
      id: taxesList?.length === 0 ? '' : defaultTax.id,
      name: taxesList?.length === 0 ? '' : defaultTax.name,
      rate: taxesList?.length === 0 ? '' : defaultTax.rate,
      amount: taxesList?.length === 0 ? '' : defaultTax.amount,
    };
    setInput({
      ...input,
      invoiceItems: input?.invoiceItems?.map((item) =>
        item.id === itemId
          ? { ...item, invoiceItemTaxes: [...item?.invoiceItemTaxes, newItem] }
          : item
      ),
    });
  };
  return (
    <>
      {props.id ? (
        <>
          <TableRow className={'no-border'}>
            <TableCell>
              <TextField
                id="description"
                size="small"
                name={`description[${props.id}]`}
                placeholder={intl.get('newInvoice.desctiption.placeholder')}
                value={props?.description}
                onChange={(e) => props.handleInputItemTableChange(e)}
                onBlur={() => handleItemFieldReview(`invoiceItems[${props.id}].description`)}
                disabled={props.disabled}
                fullWidth
                error={!!errors?.[`invoiceItems[${props.id}].description`]}
              />
            </TableCell>
            <TableCell>
              <TextField
                id="quantity"
                type="number"
                name={`quantity[${props.id}]`}
                placeholder="0"
                value={props?.quantity}
                onChange={(e) => props.handleInputItemTableChange(e)}
                onBlur={() => handleItemFieldReview(`invoiceItems[${props.id}].quantity`)}
                disabled={props.disabled}
                error={!!errors?.[`invoiceItems[${props.id}].quantity`]}
              />
            </TableCell>
            <TableCell>
              <TextField
                id="rate"
                type="number"
                name={`rate[${props.id}]`}
                placeholder="0"
                value={props?.rate}
                onChange={(e) => props.handleInputItemTableChange(e)}
                onBlur={() => handleItemFieldReview(`invoiceItems[${props.id}].rate`)}
                disabled={props.disabled}
                error={!!errors?.[`invoiceItems[${props.id}].rate`]}
              />
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  id="amount"
                  type="number"
                  name={`amount[${props.id}]`}
                  placeholder="0"
                  value={(props?.rate * props?.quantity).toFixed(2)}
                  onChange={(e) => props.handleInputItemTableChange(e)}
                  disabled={true}
                  className="w-100"
                  sx={{ m: 0, p: 0 }}
                />
                <IconButton
                  size="small"
                  onClick={() => props.handleDelete(props.id)}
                  style={{ cursor: 'pointer' }}
                  aria-label="delete"
                  color={input.invoiceItems.length === 1 ? 'inherit' : 'error'}
                  disabled={input.invoiceItems.length === 1 || props.disabled}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <ClearIcon width="24" height="24" fontSize="inherit" />
                </IconButton>
              </Box>
            </TableCell>
          </TableRow>
          <TableRow sx={{ border: '0px!important' }}>
            <TableCell></TableCell>
            <TableCell colSpan={2}>
              <Stack gap={2}>
                {invoiceItemTaxes?.map((tax, key) => {
                  return (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Select
                        id="tax"
                        onChange={(e) => handleTaxChange(e, props.id, key)}
                        onBlur={() => handleItemFieldReview(`invoiceItems[${props.id}].invoiceItemTaxes[${key + 1}].name`)}
                        value={invoiceItemTaxes[key]?.id || ''}
                        className="w-100"
                        displayEmpty
                        disabled={props.disabled}
                        renderValue={(selected) => {
                          if (selected.length === 0) {
                            return <span style={{ color: '#A0A0A0' }}>Taxes</span>;
                          }
                          return itemTaxes?.find((tax) => tax.id === selected)?.name;
                        }}
                        error={
                          !!errors?.[`invoiceItems[${props.id}].invoiceItemTaxes[${key + 1}].name`]
                        }
                      >
                        {itemTaxes?.length > 0 ? (
                          itemTaxes.map((tax, key) => {
                            return (
                              <MenuItem key={key} value={tax.id}>
                                {tax.name} [{tax.rate > 0 ? `${tax.rate}%` : `$${tax.amount}`}]
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem disabled>{intl.get('placeholder.noOptions')}</MenuItem>
                        )}
                      </Select>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTax(key)}
                        style={{ cursor: 'pointer' }}
                        aria-label="delete"
                        color="error"
                        disabled={props.disabled}
                      >
                        <ClearIcon width="24" height="24" fontSize="inherit" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Stack>
            </TableCell>
            <TableCell
              colSpan={2}
              align="right"
              style={{ padding: '0px', verticalAlign: 'bottom' }}
            >
              <Button
                variant="text"
                style={{ textDecoration: 'underline', cursor: 'pointer', padding: 4 }}
                onClick={() => handleNewTax(props.id)}
                className="h-100"
                color="inherit"
                disabled={invoiceItemTaxes?.some((tax) => tax.id === '') || props.disabled}
              >
                +{intl.get('newInvoice.add.taxes')}
              </Button>
            </TableCell>
          </TableRow>
        </>
      ) : null}
    </>
  );
};

export default NewInvoiceItemTableRow;
